import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "A TypeScript trick for reusable hooks",
  "description": "Reusable hooks are all fun and games until TypeScript starts yelling at you about incompatible types.",
  "published": "2021-02-19T00:00:00.000Z",
  "categories": "Technical, TypeScript, React",
  "hero": "./img/screenshot-1613755279449.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`How do you write a reusable React hook?`}</p>
    <p>{`Oh that's easy, `}<em parentName="p">{`every`}</em>{` hook is reusable! 🥳`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`function useMyLittleStateMachine() {
  const [state, setState] = useState("start");
  let nextState = state;

  switch (state) {
    case "start":
      nextState = "second";
      break;
    case "second":
      nextState = "third";
      break;
    case "third":
      nextState = "start";
      break;
  }

  return { state, nextState };
}
`}</code></pre>
    <p>{`You get a tiny little state machine that doesn't do much. Runs you 'round in a circle.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/a63ded1d0d78e3a1e8feed05d422c1b3/05fb0/A-tiny-3-state-state-machinecfj67g.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "67.2645739910314%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'269\\'%20viewBox=\\'0%200%20400%20269\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M73%207L58%208c-8%200-17%205-29%2015-35%2031-32%2060%209%2071%2023%206%2066%201%2085-11%2014-8%2028-25%2028-33v-3h7c13%202%2047%2011%2061%2017%2015%207%2033%2022%2044%2038%206%208%206%209%202%206-3-2-7-3-7-2l1%201c2%200%209%205%209%206l-5%202c-6%201-14%206-14%208l-4%204c-21%2016-25%2031-11%2045%2010%2010%2037%2019%2065%2021h9l-2%202-6%205-1%202%206-4c5-4%206-5%2014-5%208-1%2023-5%2033-9%2030-11%2049-34%2041-50-10-19-45-30-82-27l-32%205c-3%200-2-1%202-8l4-8-10%2015-1%201c-1%201-3-1-6-5-13-21-32-38-52-46-10-4-41-13-54-15l-11-1v-5c-2-13-12-26-23-30-13-5-36-6-55-3m7%201l-4%201-6%201c-12%200-25%203-28%207l-2%201c-2%200-17%2013-22%2019-26%2031-5%2056%2047%2056%2035%200%2058-8%2076-27%208-10%2010-19%206-31-5-16-17-24-37-27H80M42%2041c-6%200-6%202%200%207%206%207%207%209%200%209l-7-1c-4-2-5-1-2%201s13%203%2016%201c2-1%200-6-4-10s-4-4-2-5h5l3-1c0-2-1-2-9-1m53%204c-2%206-4%2012-2%2012l2-2h4c6%202%207%202%207%201l-3-2c-2-1-2-1-1-2%201-2%201-5-1-8-2-4-4-4-6%201m-20%204c-5%204-7%207-6%207l3-1c2-3%2011-1%2011%202l1%201V48l-1-6-8%207m-29%2053c-6%208-6%2010-1%204%206-7%206-6%202%2011-7%2027-4%2050%2010%2068%208%2010%2015%2016%2018%2016%202%200%202%200%200-1-3%200-24-23-22-23v-2c-5-4-10-28-9-39l7-31%202%206c1%203%202%205%203%204l-2-8-2-8c0-3-1-2-6%203m252%209c-8%201-11%202-11%203l-4%201c-6-1-9%200-9%202h-2c-4-1-5-1-12%202-9%206-22%2016-27%2023-10%2014-4%2028%2014%2036%2026%2011%2063%2016%2086%2010%2035-9%2060-28%2060-46%200-11-18-25-37-30-11-3-42-4-58-1m2%2033l-2%203c-2%200-3%202-1%203l6%201c8%200%2012-5%206-7s-8-2-9%200m-174%2027c-17%204-26%209-34%2018-11%2013-17%2024-17%2034%200%2019%2038%2039%2075%2041%2014%200%2020-1%2028-8%2020-17%2025-41%2011-62-12-18-39-28-63-23m7%202l-5%201-1%201c0%201-2%202-6%202-18%200-41%2023-43%2043-2%2010%202%2015%2016%2024%2016%2011%2037%2017%2058%2017l13%201%206-4c17-10%2027-29%2023-45-5-24-25-39-51-40h-10m159%2031c-20%209-80%2028-88%2028-2%200-2%200%203-5l7-5%202-1c-2-2-4-1-11%206-10%209-10%2010%203%2010%2019-1%2026-2%2027-3l-17%201h-19l6-1a558%20558%200%200087-30m-123%2014v8c0%202%200%203%204%203%2011%200%2013-5%205-11-7-6-8-6-9%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/a63ded1d0d78e3a1e8feed05d422c1b3/ca0a1/A-tiny-3-state-state-machinecfj67g.webp 223w", "/static/a63ded1d0d78e3a1e8feed05d422c1b3/75680/A-tiny-3-state-state-machinecfj67g.webp 445w", "/static/a63ded1d0d78e3a1e8feed05d422c1b3/8d1ba/A-tiny-3-state-state-machinecfj67g.webp 890w", "/static/a63ded1d0d78e3a1e8feed05d422c1b3/22e6c/A-tiny-3-state-state-machinecfj67g.webp 1138w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/a63ded1d0d78e3a1e8feed05d422c1b3/e92b6/A-tiny-3-state-state-machinecfj67g.png 223w", "/static/a63ded1d0d78e3a1e8feed05d422c1b3/e66bf/A-tiny-3-state-state-machinecfj67g.png 445w", "/static/a63ded1d0d78e3a1e8feed05d422c1b3/4ef49/A-tiny-3-state-state-machinecfj67g.png 890w", "/static/a63ded1d0d78e3a1e8feed05d422c1b3/05fb0/A-tiny-3-state-state-machinecfj67g.png 1138w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/a63ded1d0d78e3a1e8feed05d422c1b3/4ef49/A-tiny-3-state-state-machinecfj67g.png",
                "alt": "A tiny 3-state state machine",
                "title": "A tiny 3-state state machine",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`A tiny 3-state state machine`}</figcaption>{`
  `}</figure></p>
    <p>{`Let's pretend you're using it for routing because that's what I was building to get this idea. `}<em parentName="p">{`"What is the next step in this UX flow?"`}</em></p>
    <p>{`You use the state machine like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const GoNext = () => {
  const { nextState } = useMyLittleStateMachine();

  return <Link to={\`/important_flow/{nextState}\`}>Go Next</Link>;
};
`}</code></pre>
    <p>{`Handy way to decouple sequences of UI views. You think it's silly until your PM starts shuffling things around to run A/B tests.`}</p>
    <h2 {...{
      "id": "a-linear-state-machine-is-silly"
    }}>{`A linear state machine is silly`}</h2>
    <p>{`You wouldn't bother with a linear state machine. Make it an array, use incrementing index. Way easier.`}</p>
    <p>{`But UX flows like to depend on context.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`function useMyRoutingStateMachine(user: User) {
  // from React Router, returns current location
  const location = useLocation();

  let nextRoute = "start";

  switch (location.path) {
    case "start":
      if (user.valid) {
        nextState = "do_things";
      } else if (user.badName) {
        nextState = "fix_name";
      } else {
        nextState = "error";
      }
      break;
    case "fix_name":
      if (user.valid) {
        nextState = "do_things";
      }
    // ...
  }

  return { nextState };
}
`}</code></pre>
    <p>{`Each state checks properties on the `}<inlineCode parentName="p">{`user`}</inlineCode>{` model to decide what comes next. An error, a data-fixing form, the core functionality, ...`}</p>
    <p>{`This is great. Put it anywhere in your app, give current user, and it says what screen comes next. 🥳`}</p>
    <h2 {...{
      "id": "a-non-reusable-state-machine-phooey"
    }}>{`A non-reusable state machine, phooey`}</h2>
    <p>{`You've got this cool little state machine that works for users.`}</p>
    <p>{`What about for comments? Those are similar if you squint hard enough. The flow starts, then you can have an error, fix things ... hmmmm 🤔`}</p>
    <p>{`Yes I know I'm stretching. It's an example to show a thing.`}</p>
    <p>{`If comments and users share the important properties, you can do this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`function useMyRoutingStateMachine(context: User | Comment) {
  // from React Router, returns current location
  const location = useLocation();

  let nextRoute = "start";

  switch (location.path) {
    case "start":
      if (context.valid) {
        nextState = "do_things";
      } else if (context.badContent) {
        nextState = "fix_content";
      } else {
        nextState = "error";
      }
      break;
    case "fix_content":
      if (context.valid) {
        nextState = "do_things";
      }
    // ...
  }

  return { nextState };
}
`}</code></pre>
    <p>{`A `}<a parentName="p" {...{
        "href": "https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html#union-types"
      }}>{`union type`}</a>{` says the `}<inlineCode parentName="p">{`context`}</inlineCode>{` param should be either a `}<inlineCode parentName="p">{`User`}</inlineCode>{` or a `}<inlineCode parentName="p">{`Comment`}</inlineCode>{`. You read it like an OR, but it's not an OR.`}</p>
    <p>{`Union means the new type has all the `}<em parentName="p">{`shared`}</em>{` properties.`}</p>
    <p>{`And that's where things blow up. The more you lean into union types, the harder TypeScript has to work, the more you'll see errors like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`types of parameters 'user' and 'context' are incompatible
Type 'User | Comment' is not assignable to type 'Comment'
Type 'User' is missing the following properties from type 'Comment'
`}</code></pre>
    <p>{`💩`}</p>
    <h2 {...{
      "id": "typescript-generics-to-the-rescue"
    }}>{`TypeScript generics to the rescue`}</h2>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`I successfully generic'd my first TypeScript for a reusable hook, ama`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1362132067010502656"
      }}>{`February 17, 2021`}</a></blockquote>
    <p>{`When you're done pulling your hair out, here's what you do 👉 you sprinkle a TypeScript generic on your code. They're not as scary as they look.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`function useMyRoutingStateMachine<T>(context: T) {
  // ...
}
`}</code></pre>
    <p>{`Context is of generic type `}<inlineCode parentName="p">{`T`}</inlineCode>{` as specified when the hook is used.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const GoNextUser = (user: User) => {
  const { nextState } = useMyLittleStateMachine<User>(user);

  return <Link to={\`/important_flow/{nextState}\`}>Go Next</Link>;
};

const GoNextComment = (comment: Comment) => {
  const { nextState } = useMyLittleStateMachine<Comment>(comment);

  return <Link to={\`/important_flow/{nextState}\`}>Go Next</Link>;
};
`}</code></pre>
    <p>{`Call the hook and `}<em parentName="p">{`tell it the type of its parameter`}</em>{`. You've done this before with library hooks, at least `}<inlineCode parentName="p">{`useState`}</inlineCode>{`, but it feels special when it's your own.`}</p>
    <p>{`You can go even further. Make the component generic.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const GoNext = (context: T) => {
  const { nextState } = useMyLittleStateMachine<T>(context);

  return <Link to={\`/important_flow/{nextState}\`}>Go Next</Link>;
};
`}</code></pre>
    <p>{`🤯`}</p>
    <p>{`TypeScript doesn't care about the name of the type you're passing around. As long as its consistent.`}</p>
    <p>{`Obvious in retrospect.`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: you can use generics in type definitions and that blows my mind`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`export type FlowMachine<T> = {
  // init step is required
  init: FlowStep<T>;
  [key: string]: FlowStep<T>;
};

type FlowStep<T> = {
  path: string;
  nextStep: (context: T) => FlowStep<T>;
};
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      